import type { DeepPartial } from '../../lib/character-hud-store';

/**
 * The write target captured at ACCUMULATION time. The load-bearing invariant
 * of the token-switch race fix: a debounced diff must ship to the sheet it was
 * edited on — never to whichever sheet happens to be selected when the debounce
 * timer fires. Capturing (sheetId, rawData, send) when the edit is accumulated,
 * not when it flushes, is what makes that true.
 */
export interface FlushCtx<R> {
  sheetId: string;
  rawData: R;
  send: (msg: unknown) => void;
}

/**
 * Owns the debounced write's accumulated diff + its captured target, decoupled
 * from the overlay's React state (optimistic pendingPatch, write-timeout) so the
 * cross-sheet-write invariant can be unit-tested. `H` is the HUD shape, `R` the
 * rules shape toCommands consumes (HUD widens to it structurally).
 */
export function createFlushAccumulator<H, R>(
  toCommands: (diff: DeepPartial<R>, rawData: R, sheetId: string) => unknown[],
  merge: (a: DeepPartial<H>, b: DeepPartial<H>) => DeepPartial<H>,
) {
  let acc: DeepPartial<H> | null = null;
  let ctx: FlushCtx<R> | null = null;

  return {
    /** Merge a new edit and (re)capture its target. Later edits before a flush
     * update the target — so a same-sheet burst coalesces, and a switch
     * mid-burst re-points subsequent edits at the new sheet. */
    accumulate(diff: DeepPartial<H>, nextCtx: FlushCtx<R>): void {
      acc = acc ? merge(acc, diff) : diff;
      ctx = nextCtx;
    },

    /** Build commands for the CAPTURED target, send them, clear, and return
     * them (the caller uses the returned set to decide whether to arm the
     * write-timeout). No-op with [] when nothing is pending. */
    flush(): unknown[] {
      const pendingDiff = acc;
      const target = ctx;
      acc = null;
      ctx = null;
      if (!pendingDiff || !target) return [];
      const cmds = toCommands(pendingDiff as unknown as DeepPartial<R>, target.rawData, target.sheetId);
      for (const cmd of cmds) target.send(cmd);
      return cmds;
    },

    hasPending(): boolean {
      return acc !== null;
    },
  };
}

export type FlushAccumulator<H, R> = ReturnType<typeof createFlushAccumulator<H, R>>;
